import { createI18n } from 'vue-i18n'

/**
 * Two languages, one product. Keys are grouped by screen under src/locales/<lang>/*.json,
 * every English file has an Arabic twin with the same keys (tests/i18nParity.spec.ts
 * fails otherwise), and the key itself is never shown: a missing key falls back to
 * English, never to `views.watches.title`.
 *
 * Numbers stay Latin in both languages (an operations console reads 1,234 the same way
 * in Cairo and in London); dates and relative times follow the language.
 */
export type Locale = 'en' | 'ar'
export const LOCALES: readonly Locale[] = ['en', 'ar']
export const LOCALE_STORAGE_KEY = 'uobserve.locale'

const en = import.meta.glob('../locales/en/*.json', { eager: true, import: 'default' }) as Record<string, object>
const ar = import.meta.glob('../locales/ar/*.json', { eager: true, import: 'default' }) as Record<string, object>

function merge(files: Record<string, object>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [path, messages] of Object.entries(files)) {
    const name = path.split('/').pop()!.replace(/\.json$/, '')
    out[name] = messages
  }
  return out
}

export function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
    if (stored === 'ar' || stored === 'en') return stored
    if (typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('ar')) return 'ar'
  } catch {
    /* storage disabled */
  }
  return 'en'
}

export const i18n = createI18n<false>({
  legacy: false,
  locale: readStoredLocale(),
  fallbackLocale: 'en',
  messages: { en: merge(en), ar: merge(ar) } as Record<Locale, Record<string, never>>,
  missingWarn: false,
  fallbackWarn: false,
  // Arabic has six plural forms in the language, two in this console: the rule picks
  // `one | other` the way English does, so one message file shape serves both.
  pluralRules: { ar: (n: number, choices: number) => (choices <= 2 ? (n === 1 ? 0 : 1) : n === 0 ? 0 : n === 1 ? 1 : 2) },
})

/** Direction of a language: the only thing layout needs to know. */
export function dirOf(locale: string): 'rtl' | 'ltr' {
  return locale === 'ar' ? 'rtl' : 'ltr'
}

/** The intl tag for Date/Number formatting in a language, Latin digits in both. */
export function intlTag(locale: string): string {
  return locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB'
}

/** `t` outside a component (router titles, formatters): plain strings in, plain strings out. */
export function translate(key: string, values?: Record<string, unknown>): string {
  const t = i18n.global.t as unknown as (k: string, v?: Record<string, unknown>) => string
  return values ? t(key, values) : t(key)
}

export function currentLocale(): Locale {
  return i18n.global.locale.value as Locale
}
